'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var hdShared = require('@onekeyfe/hd-shared');
var transport = require('@onekeyfe/hd-transport');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var transport__default = /*#__PURE__*/_interopDefaultLegacy(transport);

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const { check, ProtocolV1, parseConfigure } = transport__default["default"];
const PROTOCOL_PROBE_TIMEOUT_MS = 1000;
const PROTOCOL_V2_PROBE_TIMEOUT_MS = 5000;
const LOWLEVEL_PROTOCOL_V2_PACKET_LENGTH_FALLBACK = 192;
const LOWLEVEL_PROTOCOL_V2_PACKET_LENGTH_MAX = 244;
const FIRMWARE_UPLOAD_LOG_PERCENT_STEP = 5;
const FIRMWARE_UPLOAD_LOG_INTERVAL_MS = 10000;
function shouldLogFirmwareUploadProgress({ percent, lastLoggedPercent, now, lastLoggedAt, }) {
    return (percent === 100 ||
        percent - lastLoggedPercent >= FIRMWARE_UPLOAD_LOG_PERCENT_STEP ||
        now - lastLoggedAt >= FIRMWARE_UPLOAD_LOG_INTERVAL_MS);
}
function getProtocolV1SendOptions(name) {
    return name === 'FirmwareUpload' ? { withoutResponse: false } : undefined;
}
function resolveLowlevelProtocolV2PacketCapacity(capacity) {
    if (typeof capacity !== 'number' || !Number.isFinite(capacity) || capacity <= 0) {
        return LOWLEVEL_PROTOCOL_V2_PACKET_LENGTH_FALLBACK;
    }
    return Math.min(Math.floor(capacity), LOWLEVEL_PROTOCOL_V2_PACKET_LENGTH_MAX);
}
function isProtocolV1TransportChunk(data) {
    return data.length >= 9 && data[0] === 0x3f && data[1] === 0x23 && data[2] === 0x23;
}
function readProtocolV1PayloadLength(data) {
    return data[5] * 0x1000000 + data[6] * 0x10000 + data[7] * 0x100 + data[8];
}
class LowlevelTransport {
    constructor() {
        this.configured = false;
        this.plugin = {};
        this.deviceProtocol = new Map();
        this.deviceProtocolHints = new Map();
        this.protocolV2Assemblers = new Map();
        this.protocolV2Generations = new Map();
        this.connectedDevices = new Set();
        this.protocolV2PacketCapacities = new Map();
        this.protocolV2Links = new transport.ProtocolV2LinkManager({
            getSchemas: () => {
                if (!this._messages || !this._messagesV2) {
                    throw hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.TransportNotConfigured);
                }
                return {
                    protocolV1: this._messages,
                    protocolV2: this._messagesV2,
                };
            },
            classifyError: () => 'link-fatal',
            onLinkInvalidated: (uuid, reason) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c;
                (_a = this.protocolV2Assemblers.get(uuid)) === null || _a === void 0 ? void 0 : _a.reset();
                (_b = this.Log) === null || _b === void 0 ? void 0 : _b.debug(`[LowlevelTransport] Protocol V2 link invalidated: ${uuid}`, reason);
                if (reason.startsWith('Protocol V2 link-fatal error:')) {
                    this.deviceProtocol.delete(uuid);
                    this.advanceProtocolV2Generation(uuid);
                    try {
                        yield this.plugin.disconnect(uuid);
                    }
                    catch (error) {
                        (_c = this.Log) === null || _c === void 0 ? void 0 : _c.debug(`[LowlevelTransport] disconnect tainted Protocol V2 link failed: ${uuid}`, error);
                    }
                    finally {
                        this.connectedDevices.delete(uuid);
                        this.protocolV2PacketCapacities.delete(uuid);
                    }
                }
            }),
        });
    }
    getProtocolType(path) {
        return this.deviceProtocol.get(path);
    }
    init(logger, emitter, plugin) {
        this.Log = logger;
        this.emitter = emitter;
        this.plugin = plugin;
        this.plugin.init();
    }
    configure(signedData) {
        const messages = parseConfigure(signedData);
        this.configured = true;
        this._messages = messages;
    }
    configureProtocolV2(signedData) {
        const configuration = typeof signedData === 'string' ? signedData : JSON.stringify(signedData);
        if (this.protocolV2SchemaConfiguration === configuration) {
            return;
        }
        const isReconfiguration = this.protocolV2SchemaConfiguration !== undefined;
        this._messagesV2 = parseConfigure(signedData);
        this.protocolV2SchemaConfiguration = configuration;
        if (isReconfiguration) {
            this.protocolV2Links
                .invalidateAllLinks('Protocol V2 schema reconfigured')
                .catch(error => { var _a; return (_a = this.Log) === null || _a === void 0 ? void 0 : _a.debug('Protocol V2 schema link cleanup failed:', error); });
        }
    }
    listen() {
    }
    enumerate() {
        return __awaiter(this, void 0, void 0, function* () {
            const devices = yield this.plugin.enumerate();
            return devices;
        });
    }
    acquire(input) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const alreadyConnected = this.connectedDevices.has(input.uuid);
            try {
                yield this.plugin.connect(input.uuid);
                yield this.refreshProtocolV2PacketCapacity(input.uuid);
                if (!alreadyConnected) {
                    this.connectedDevices.add(input.uuid);
                    this.advanceProtocolV2Generation(input.uuid);
                }
            }
            catch (error) {
                this.connectedDevices.delete(input.uuid);
                this.Log.debug('lowlelvel transport connect error: ', error);
                throw hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.LowlevelTrasnportConnectError, (_a = error.message) !== null && _a !== void 0 ? _a : error);
            }
            this.protocolV2Assemblers.set(input.uuid, new transport.ProtocolV2FrameAssembler(transport.PROTOCOL_V2_BLE_FRAME_MAX_BYTES));
            const protocolHint = input.expectedProtocol
                ? undefined
                : (_b = input.protocolHint) !== null && _b !== void 0 ? _b : this.deviceProtocolHints.get(input.uuid);
            try {
                const protocolType = yield this.detectProtocol(input.uuid, input.expectedProtocol, protocolHint);
                return { uuid: input.uuid, protocolType };
            }
            catch (error) {
                try {
                    yield this.protocolV2Links.invalidateLink(input.uuid, 'Lowlevel transport acquire failed');
                }
                catch (cleanupError) {
                    (_c = this.Log) === null || _c === void 0 ? void 0 : _c.debug('[LowlevelTransport] acquire link cleanup failed:', cleanupError);
                }
                try {
                    yield this.plugin.disconnect(input.uuid);
                }
                catch (cleanupError) {
                    (_d = this.Log) === null || _d === void 0 ? void 0 : _d.debug('[LowlevelTransport] acquire disconnect failed:', cleanupError);
                }
                finally {
                    this.connectedDevices.delete(input.uuid);
                    this.deviceProtocol.delete(input.uuid);
                    this.protocolV2Assemblers.delete(input.uuid);
                    this.protocolV2PacketCapacities.delete(input.uuid);
                    this.advanceProtocolV2Generation(input.uuid);
                }
                throw error;
            }
        });
    }
    release(uuid) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.protocolV2Links.invalidateLink(uuid, 'Lowlevel transport released');
                yield this.plugin.disconnect(uuid);
                this.connectedDevices.delete(uuid);
                this.deviceProtocol.delete(uuid);
                this.protocolV2Assemblers.delete(uuid);
                this.protocolV2PacketCapacities.delete(uuid);
                return true;
            }
            catch (error) {
                this.Log.debug('lowlelvel transport disconnect error: ', error);
                return false;
            }
        });
    }
    call(uuid, name, data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._messages === null || !this._messages) {
                throw hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.TransportNotConfigured);
            }
            const protocol = this.getProtocolType(uuid);
            if (!protocol) {
                throw hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.RuntimeError, `Device protocol has not been detected for ${uuid}`);
            }
            if (protocol === 'V2') {
                return this.callProtocolV2(uuid, name, data, options);
            }
            return this.callProtocolV1(uuid, name, data, options);
        });
    }
    post(uuid, name, data) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.getProtocolType(uuid) === 'V2') {
                yield this.protocolV2Links.sendFlowControl(uuid, () => this.createProtocolV2Adapter(uuid), name, data);
                return;
            }
            yield this.callProtocolV1(uuid, name, data);
        });
    }
    callProtocolV1(uuid, name, data, options) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._messages) {
                throw hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.TransportNotConfigured);
            }
            const messages = this._messages;
            const buffers = ProtocolV1.encodeTransportPackets(messages, name, data);
            const isFirmwareUpload = name === 'FirmwareUpload';
            const uploadStartedAt = Date.now();
            const totalBytes = buffers.reduce((sum, buffer) => sum + buffer.limit, 0);
            let sentBytes = 0;
            let lastLoggedPercent = 0;
            let lastLoggedAt = uploadStartedAt;
            for (const [index, o] of buffers.entries()) {
                const outData = o.toString('hex');
                try {
                    yield this.plugin.send(uuid, outData, getProtocolV1SendOptions(name));
                    sentBytes += o.limit;
                    if (isFirmwareUpload) {
                        const now = Date.now();
                        const percent = Math.floor(((index + 1) / buffers.length) * 100);
                        if (shouldLogFirmwareUploadProgress({
                            percent,
                            lastLoggedPercent,
                            now,
                            lastLoggedAt,
                        })) {
                            const elapsedSeconds = Math.max((now - uploadStartedAt) / 1000, 0.001);
                            const kibPerSecond = sentBytes / 1024 / elapsedSeconds;
                            (_a = this.Log) === null || _a === void 0 ? void 0 : _a.debug(`[LowlevelTransport] FirmwareUpload progress: ${percent}% ` +
                                `(${index + 1}/${buffers.length} packets, ${sentBytes}/${totalBytes} bytes, ` +
                                `${elapsedSeconds.toFixed(1)}s, ${kibPerSecond.toFixed(1)} KiB/s)`);
                            lastLoggedPercent = percent;
                            lastLoggedAt = now;
                        }
                    }
                }
                catch (e) {
                    this.Log.debug('lowlevel transport send error: ', e);
                    throw hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.BleWriteCharacteristicError, e.reason);
                }
            }
            try {
                const response = yield this.readProtocolV1Message(uuid, options === null || options === void 0 ? void 0 : options.timeoutMs);
                const jsonData = ProtocolV1.decodeMessage(messages, response);
                return check.call(jsonData);
            }
            catch (e) {
                if ((e === null || e === void 0 ? void 0 : e.errorCode) === hdShared.HardwareErrorCode.BleTimeoutError &&
                    (options === null || options === void 0 ? void 0 : options.timeoutMs) !== PROTOCOL_PROBE_TIMEOUT_MS) {
                    try {
                        yield this.resetConnectionAfterProbe(uuid, 'V1');
                    }
                    catch (resetError) {
                        this.Log.debug('[LowlevelTransport] reset after Protocol V1 timeout failed:', resetError);
                    }
                }
                if (name === 'Initialize' && (options === null || options === void 0 ? void 0 : options.timeoutMs) === PROTOCOL_PROBE_TIMEOUT_MS) {
                    this.Log.debug('[LowlevelTransport] Protocol V1 Initialize probe call failed:', e);
                }
                else {
                    this.Log.error('lowlevel call error: ', e);
                }
                throw e;
            }
        });
    }
    createProtocolTimeoutError(name, timeout) {
        return hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.BleTimeoutError, `Lowlevel response timeout after ${timeout}ms for ${name}`);
    }
    createProtocolMismatchError(expected) {
        return hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.RuntimeError, `Device protocol mismatch: expected ${expected}, but device did not respond to expected protocol`);
    }
    createProtocolDetectionError() {
        return hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.BleTimeoutError, 'Unable to detect BLE protocol: device did not respond to Protocol V1 Initialize or Protocol V2 Ping');
    }
    clearProbeProtocol(uuid, protocol) {
        if (this.deviceProtocol.get(uuid) === protocol) {
            this.deviceProtocol.delete(uuid);
        }
    }
    detectProtocol(uuid, expectedProtocol, protocolHint) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (expectedProtocol === 'V2') {
                if (yield this.probeProtocolV2(uuid)) {
                    this.deviceProtocol.set(uuid, 'V2');
                    (_a = this.Log) === null || _a === void 0 ? void 0 : _a.debug(`[LowlevelTransport] detectProtocol: uuid=${uuid} -> V2 (expected)`);
                    return 'V2';
                }
                throw this.createProtocolMismatchError(expectedProtocol);
            }
            if (expectedProtocol === 'V1') {
                if (yield this.probeProtocolV1(uuid)) {
                    this.deviceProtocol.set(uuid, 'V1');
                    (_b = this.Log) === null || _b === void 0 ? void 0 : _b.debug(`[LowlevelTransport] detectProtocol: uuid=${uuid} -> V1 (expected)`);
                    return 'V1';
                }
                throw this.createProtocolMismatchError(expectedProtocol);
            }
            const probeOrder = protocolHint === 'V2' || this.deviceProtocol.get(uuid) === 'V2' ? ['V2', 'V1'] : ['V1', 'V2'];
            for (const [index, protocol] of probeOrder.entries()) {
                if (index > 0) {
                    yield this.resetConnectionAfterProbe(uuid, probeOrder[index - 1]);
                }
                const detected = protocol === 'V1' ? yield this.probeProtocolV1(uuid) : yield this.probeProtocolV2(uuid);
                if (detected) {
                    this.deviceProtocol.set(uuid, protocol);
                    (_c = this.Log) === null || _c === void 0 ? void 0 : _c.debug(`[LowlevelTransport] detectProtocol: uuid=${uuid} -> ${protocol}`);
                    return protocol;
                }
            }
            this.deviceProtocol.delete(uuid);
            throw this.createProtocolDetectionError();
        });
    }
    resetConnectionAfterProbe(uuid, protocol) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            yield this.protocolV2Links.invalidateLink(uuid, `Reset connection after Protocol ${protocol} probe`);
            (_a = this.protocolV2Assemblers.get(uuid)) === null || _a === void 0 ? void 0 : _a.reset();
            try {
                this.connectedDevices.delete(uuid);
                yield this.plugin.disconnect(uuid);
            }
            catch (error) {
                (_b = this.Log) === null || _b === void 0 ? void 0 : _b.debug(`[LowlevelTransport] disconnect after Protocol ${protocol} probe failed:`, error);
            }
            try {
                yield this.plugin.connect(uuid);
                yield this.refreshProtocolV2PacketCapacity(uuid);
                this.connectedDevices.add(uuid);
                this.advanceProtocolV2Generation(uuid);
            }
            catch (error) {
                (_c = this.Log) === null || _c === void 0 ? void 0 : _c.debug(`[LowlevelTransport] reconnect after Protocol ${protocol} probe failed:`, error);
                throw hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.LowlevelTrasnportConnectError, (_d = error.message) !== null && _d !== void 0 ? _d : error);
            }
        });
    }
    probeProtocolV1(uuid) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._messages) {
                return false;
            }
            try {
                this.deviceProtocol.set(uuid, 'V1');
                yield this.callProtocolV1(uuid, 'Initialize', {}, { timeoutMs: PROTOCOL_PROBE_TIMEOUT_MS });
                return true;
            }
            catch (error) {
                this.clearProbeProtocol(uuid, 'V1');
                (_a = this.Log) === null || _a === void 0 ? void 0 : _a.debug('[LowlevelTransport] Protocol V1 Initialize probe failed:', error);
                return false;
            }
        });
    }
    probeProtocolV2(uuid) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._messages || !this._messagesV2) {
                return false;
            }
            this.deviceProtocol.set(uuid, 'V2');
            (_a = this.protocolV2Assemblers.get(uuid)) === null || _a === void 0 ? void 0 : _a.reset();
            try {
                const detected = yield transport.probeProtocolV2({
                    call: (name, data, options) => this.callProtocolV2(uuid, name, data, options),
                    timeoutMs: PROTOCOL_V2_PROBE_TIMEOUT_MS,
                    logger: this.Log,
                    logPrefix: 'ProtocolV2 Lowlevel-BLE',
                    onProbeFailed: () => __awaiter(this, void 0, void 0, function* () {
                        var _b;
                        (_b = this.protocolV2Assemblers.get(uuid)) === null || _b === void 0 ? void 0 : _b.reset();
                        yield this.resetConnectionAfterProbe(uuid, 'V2');
                    }),
                });
                if (!detected) {
                    this.clearProbeProtocol(uuid, 'V2');
                }
                return detected;
            }
            catch (error) {
                this.clearProbeProtocol(uuid, 'V2');
                throw error;
            }
        });
    }
    receiveHex(uuid, timeoutMs, commandName) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield transport.withProtocolTimeout(this.plugin.receive(uuid), timeoutMs, () => this.createProtocolTimeoutError(commandName, timeoutMs !== null && timeoutMs !== void 0 ? timeoutMs : 0));
            if (typeof response !== 'string') {
                throw new Error('Returning data is not string');
            }
            return response;
        });
    }
    readProtocolV1Message(uuid, timeoutMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const first = yield this.receiveHex(uuid, timeoutMs, 'ProtocolV1');
            const firstData = transport.hexToBytes(first);
            if (!isProtocolV1TransportChunk(firstData)) {
                return first;
            }
            const payloadLength = readProtocolV1PayloadLength(firstData);
            let buffer = firstData.slice(3);
            const expectedLength = transport.PROTOCOL_V1_MESSAGE_HEADER_SIZE + payloadLength;
            while (buffer.length < expectedLength) {
                const next = yield this.receiveHex(uuid, timeoutMs, 'ProtocolV1');
                buffer = transport.concatUint8Arrays([buffer, transport.hexToBytes(next)]);
            }
            return transport.bytesToHex(buffer.slice(0, expectedLength));
        });
    }
    readProtocolV2Frame(uuid, timeoutMs, commandName = 'ProtocolV2') {
        return __awaiter(this, void 0, void 0, function* () {
            let assembler = this.protocolV2Assemblers.get(uuid);
            if (!assembler) {
                assembler = new transport.ProtocolV2FrameAssembler(transport.PROTOCOL_V2_BLE_FRAME_MAX_BYTES);
                this.protocolV2Assemblers.set(uuid, assembler);
            }
            const queuedFrame = assembler.push(new Uint8Array(0));
            if (queuedFrame)
                return queuedFrame;
            let frame;
            while (!frame) {
                const response = yield this.receiveHex(uuid, timeoutMs, commandName);
                const chunk = transport.hexToBytes(response);
                if (chunk.length > 0) {
                    frame = assembler.push(chunk);
                }
            }
            return frame;
        });
    }
    writeProtocolV2Frame(uuid, frame, context, assertCurrentGeneration) {
        return __awaiter(this, void 0, void 0, function* () {
            yield transport.writeProtocolV2BleFrame({
                frame,
                packetCapacity: resolveLowlevelProtocolV2PacketCapacity(this.protocolV2PacketCapacities.get(uuid)),
                assertActive: assertCurrentGeneration,
                signal: context.signal,
                abortMessage: `Protocol V2 BLE write aborted for ${context.messageName}`,
                writePacket: packet => this.plugin.send(uuid, transport.bytesToHex(packet)),
            });
        });
    }
    refreshProtocolV2PacketCapacity(uuid) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            let reportedCapacity;
            try {
                reportedCapacity = yield ((_b = (_a = this.plugin).getProtocolV2PacketCapacity) === null || _b === void 0 ? void 0 : _b.call(_a, uuid));
            }
            catch (error) {
                (_c = this.Log) === null || _c === void 0 ? void 0 : _c.debug(`[LowlevelTransport] read Protocol V2 packet capacity failed: ${uuid}`, error);
            }
            this.protocolV2PacketCapacities.set(uuid, resolveLowlevelProtocolV2PacketCapacity(reportedCapacity));
        });
    }
    callProtocolV2(uuid, name, data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._messages || !this._messagesV2) {
                throw hdShared.ERRORS.TypedError(hdShared.HardwareErrorCode.TransportNotConfigured);
            }
            try {
                return yield this.protocolV2Links.call(uuid, () => this.createProtocolV2Adapter(uuid), name, data, options);
            }
            catch (e) {
                this.Log.error('lowlevel Protocol V2 call error: ', e);
                throw e;
            }
        });
    }
    createProtocolV2Adapter(uuid) {
        var _a;
        const generation = (_a = this.protocolV2Generations.get(uuid)) !== null && _a !== void 0 ? _a : 0;
        const assertCurrentGeneration = () => {
            if (this.protocolV2Generations.get(uuid) !== generation) {
                throw new Error(`Protocol V2 connection generation changed for ${uuid}`);
            }
        };
        return {
            router: transport.PROTOCOL_V2_CHANNEL_BLE_UART,
            maxFrameBytes: transport.PROTOCOL_V2_BLE_FRAME_MAX_BYTES,
            generation,
            prepareCall: () => {
                var _a;
                assertCurrentGeneration();
                (_a = this.protocolV2Assemblers.get(uuid)) === null || _a === void 0 ? void 0 : _a.reset();
            },
            writeFrame: (frame, context) => this.writeProtocolV2Frame(uuid, frame, context, assertCurrentGeneration),
            readFrame: (context) => {
                assertCurrentGeneration();
                return this.readProtocolV2Frame(uuid, context.timeoutMs, context.messageName);
            },
            reset: () => {
                var _a;
                (_a = this.protocolV2Assemblers.get(uuid)) === null || _a === void 0 ? void 0 : _a.reset();
            },
            logger: this.Log,
            logPrefix: 'ProtocolV2 Lowlevel-BLE',
            createTimeoutError: (messageName, timeout) => this.createProtocolTimeoutError(messageName, timeout),
        };
    }
    advanceProtocolV2Generation(uuid) {
        var _a;
        const nextGeneration = ((_a = this.protocolV2Generations.get(uuid)) !== null && _a !== void 0 ? _a : 0) + 1;
        this.protocolV2Generations.set(uuid, nextGeneration);
        return nextGeneration;
    }
    cancel() {
        this.Log.debug('lowlevel-transport', 'cancel');
    }
}

exports["default"] = LowlevelTransport;
exports.getProtocolV1SendOptions = getProtocolV1SendOptions;
exports.resolveLowlevelProtocolV2PacketCapacity = resolveLowlevelProtocolV2PacketCapacity;
exports.shouldLogFirmwareUploadProgress = shouldLogFirmwareUploadProgress;
